import PHONE_COUNTRY_CODES from './phone-country-codes.js';
import { createCountryListbox, focusVerificationError } from './phone-country-listbox.js';
import {
    composeE164 as composePhoneNumber,
    splitInternationalNumber,
} from './phone-number-composer.js';
import {
    apiFailure,
    confirmWithCachedToken,
    createCooldownController,
    createSendRecoveryCoordinator,
    FIREBASE_MESSAGE_KEYS,
    hasSmsAlternative,
    matchesSessionPhone,
    offeredChannels,
    setControlsDisabled,
    HTTP_TOO_MANY_REQUESTS,
    startPhoneSendAttempt,
    verifierFailureReport,
    withDeadline,
} from './phone-verification-flow.js';
import {
    safeStorageGet,
    safeStorageRemove,
    safeStorageSet,
    SESSION_STORAGE,
} from './safe-storage.js';
import { formatDurationShort } from './time-utils.js';
import { getApiToken } from './tokenUtils.js';
import {
    createPhoneVerificationRecovery,
    formatPhoneRetryDuration,
} from './phone-verification-recovery.js';
import { createQrSvg } from './qr-code.js';
import {
    createReverseVerification,
    isReverseLeadCountry,
    isReverseOnlyCountry,
    REVERSE_STORAGE_KEY,
} from './reverse-verification.js';

(function () {
    'use strict';

    const ACCOUNT_URL = '/app/account';
    const FLAG_BASE = '/images/circle-flags/flags/';
    const DEFAULT_COUNTRY_ISO = 'us';

    // Where several countries share one dial code, name the one a typed prefix
    // should resolve to. Without this the first alphabetical entry wins, which
    // shows Kazakhstan for +7 and Guadeloupe for +590.
    const PRIMARY_DIAL_ISO = {
        1: 'us',
        7: 'ru',
        39: 'it',
        44: 'gb',
        47: 'no',
        61: 'au',
        212: 'ma',
        262: 're',
        290: 'sh',
        358: 'fi',
        590: 'gp',
        599: 'cw',
    };

    // The fallback channels the page can offer, keyed by the name the server
    // uses. The server decides which of these it names and in what order, so a
    // name with no entry here is skipped rather than drawn as a blank button.
    const FALLBACK_CHANNEL_BUTTON_IDS = {
        whatsapp: 'verify-phone-fallback-whatsapp',
        sms: 'verify-phone-fallback-sms',
    };
    const FALLBACK_CHANNELS = Object.keys(FALLBACK_CHANNEL_BUTTON_IDS);

    // The channels the user answers by sending us a token. They have no channel
    // button, so they stay out of FALLBACK_CHANNELS and open the reverse card
    // instead.
    const REVERSE_FALLBACK_CHANNELS = ['reverse_whatsapp', 'reverse_telegram'];

    // Whether the server's offer names a paid send this build can make. The
    // corridor is derived once, on the server, from the number it was sent, so
    // the offer answers for the number Firebase would bill. A name this build
    // does not know counts as no paid send, because it reaches here as the same
    // result as an absent field. Absent, empty and unreadable all refuse.
    function offerCarriesPaidSend(channels) {
        return Array.isArray(channels)
            && channels.some((channel) => FALLBACK_CHANNELS.indexOf(channel) !== -1);
    }

    // A channel reads differently as the action we want taken and as the
    // alternative below it, so each one carries both labels. The values are
    // message-map keys, filled by the server in the viewer's language.
    const FALLBACK_LABEL_KEYS = {
        whatsapp: { primary: 'fallback_whatsapp', secondary: 'fallback_whatsapp_secondary' },
        sms: { primary: 'fallback_sms', secondary: 'fallback_sms_secondary' },
    };

    const FALLBACK_SENT_KEYS = {
        whatsapp: 'fallback_sent_whatsapp',
        sms: 'fallback_sent_sms',
    };

    // Written out in full because Tailwind's JIT scans this file for literals,
    // so a class assembled from fragments would never be generated. The primary
    // treatment matches the page's own send button.
    const FALLBACK_PRIMARY_CLASSES = [
        'bg-sky-500', 'text-white', 'shadow-md',
        'hover:bg-sky-600', 'active:bg-sky-700',
        'dark:bg-sky-600', 'dark:hover:bg-sky-500', 'dark:active:bg-sky-700',
    ];
    const FALLBACK_SECONDARY_CLASSES = [
        'border-2', 'border-sky-300', 'bg-white', 'text-sky-700',
        'hover:bg-sky-100', 'active:bg-sky-200',
        'dark:border-sky-700', 'dark:bg-gray-800', 'dark:text-sky-200',
        'dark:hover:bg-sky-900/50', 'dark:active:bg-sky-900',
    ];

    // The dial codes ship with the bundle, the labels come from the server in
    // the viewer's language, so both the display order and the search haystack
    // are rebuilt whenever the labels are applied.
    let countries = PHONE_COUNTRY_CODES.slice();
    let countriesByDialLength = [];

    function reindexCountries() {
        countries.sort((a, b) => a.name.localeCompare(b.name));
        // Longest dial code first so real multi-digit codes win over a shorter
        // prefix. Countries in the +1 numbering plan stay a picker choice
        // because their area codes are part of the national number.
        countriesByDialLength = countries.slice().sort((a, b) => b.dial.length - a.dial.length);
    }

    /** @param {Object<string, string>} names ISO-3166-1 alpha-2 (uppercase) to label. */
    function applyCountryNames(names) {
        countries = PHONE_COUNTRY_CODES.map((country) => {
            const localized = names[country.iso.toUpperCase()];

            return localized ? { ...country, name: localized } : country;
        });

        reindexCountries();
    }

    reindexCountries();

    function countryByIso(iso) {
        return countries.find((c) => c.iso === iso) || null;
    }

    function countryForDial(dialDigits) {
        if (!dialDigits) return null;

        const match = countriesByDialLength.find((c) => dialDigits.startsWith(c.dial));
        if (!match) return null;

        const primaryIso = PRIMARY_DIAL_ISO[match.dial];

        return (primaryIso && countryByIso(primaryIso)) || match;
    }

    function digitsOnly(value) {
        return String(value || '').replace(/\D/g, '');
    }

    const PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;
    const CODE_PATTERN = /^\d{6}$/;
    const RESEND_COOLDOWN_SECONDS = 60;
    const RATE_LIMITED = 'rate_limited';
    const BUDGET_EXHAUSTED = 'budget_exhausted';
    const RESEND_COOLDOWN = 'resend_cooldown';
    const BLOCKED = 'blocked';
    // The server has no App Check site key, so the check cannot run. Kept apart
    // from BLOCKED: the cause is ours, and the user has nothing to turn off.
    const UNCONFIGURED = 'unconfigured';
    // The gate is no longer pending: waived, already verified, or gone.
    const INVALID_STATE = 'invalid_state';
    const HTTP_CONFLICT = 409;
    const HTTP_SERVICE_UNAVAILABLE = 503;
    const STALLED_JOIN_RETRY_MS = 500;
    const COOLDOWN_STORAGE_KEY = 'phone-verification-cooldown-until';
    const RETRYABLE_SESSION_STORAGE_KEY = 'phone-verification-retryable-session';
    // A session the server already charged for, which no send has spent yet.
    // It survives a reload so a client whose Firebase environment is broken
    // cannot spend one of the three daily sessions on every page load.
    // Must stay under PhoneVerificationService::SESSION_TTL_SECONDS, so a
    // restored nonce is never spent after the server has dropped it.
    const RETRYABLE_SESSION_TTL_MS = 8 * 60 * 1000;
    const LIMIT_KIND_STORAGE_KEY = 'phone-verification-limit-kind';
    const PENDING_STORAGE_KEY = 'phone-verification-pending-until';
    const FALLBACK_STORAGE_KEY = 'phone-verification-fallback-until';
    // api.js serves a small stub that defines render() before the full library
    // has arrived, so readiness is taken from the documented onload callback
    // rather than from the presence of render().
    const RECAPTCHA_READY_CALLBACK = '__interpalsRecaptchaReady';
    const RECAPTCHA_MIRROR_SRC = 'https://www.recaptcha.net/recaptcha/api.js'
        + '?render=explicit&onload=' + RECAPTCHA_READY_CALLBACK;
    const RECAPTCHA_LOAD_TIMEOUT_MS = 8000;
    const PHASE_TIMEOUT_MS = 8000;
    // Bounds the cold verifier load before a send: the mirror script under its
    // own RECAPTCHA_LOAD_TIMEOUT_MS, App Check, and the widget render.
    const VERIFIER_READY_TIMEOUT_MS = 20000;
    // reCAPTCHA keeps the challenge frame in the page and toggles its visibility.
    const RECAPTCHA_CHALLENGE_SELECTOR = 'iframe[src*="/recaptcha/"][src*="/bframe"]';
    const SESSION_LIFETIME_MS = 10 * 60 * 1000;
    const CODE_LENGTH = 6;

    // Firebase's own throttle and quota map onto the two informational boxes
    // rather than the red error box.
    const FIREBASE_RATE_LIMITED = 'auth/too-many-requests';
    const FIREBASE_QUOTA_EXCEEDED = 'auth/quota-exceeded';

    // Failures are told apart by `code` rather than by message text, so the
    // handler can pick the message the user is shown.
    function codedError(code, retryAfter) {
        const error = new Error(code);
        error.code = code;
        if (retryAfter) {
            error.retryAfter = retryAfter;
        }

        return error;
    }

    function ready(fn) {
        if (document.readyState !== 'loading') {
            fn();
            return;
        }

        document.addEventListener('DOMContentLoaded', fn);
    }

    function parseJsonAttribute(root, attribute) {
        try {
            return JSON.parse(root.getAttribute(attribute) || '{}');
        } catch (e) {
            return {};
        }
    }

    function createBody(values) {
        const body = new FormData();
        Object.entries(values).forEach(([key, value]) => {
            body.append(key, String(value));
        });

        return body;
    }

    async function post(endpoint, values) {
        if (!window.ApiClient) {
            throw new Error('api_client_missing');
        }

        return window.ApiClient.request(endpoint, {
            method: 'POST',
            body: createBody(values),
        });
    }

    async function get(endpoint, options = {}) {
        if (!window.ApiClient) {
            throw new Error('api_client_missing');
        }

        return window.ApiClient.request(endpoint, { method: 'GET', ...options });
    }

    async function responseJson(response) {
        try {
            return await response.json();
        } catch (e) {
            return {};
        }
    }

    function setHidden(element, hidden) {
        if (element) {
            element.classList.toggle('hidden', hidden);
        }
    }

    function setBusy(button, spinnerId, busy) {
        if (button) {
            button.disabled = busy;
        }

        setHidden(document.getElementById(spinnerId), !busy);
    }

    ready(function () {
        const root = document.getElementById('verify-phone-root');
        if (!root) return;

        window.LucideIcons?.initialize(root);

        const config = parseJsonAttribute(root, 'data-firebase-config');
        const firebaseAppCheckSiteKey = root.getAttribute('data-firebase-app-check-site-key') || '';
        const messages = parseJsonAttribute(root, 'data-messages');
        applyCountryNames(parseJsonAttribute(root, 'data-country-names'));
        const redirectUrl = root.getAttribute('data-redirect') || ACCOUNT_URL;
        const accountId = root.getAttribute('data-account-id') || '';
        const accountStorageKey = (key) => accountId ? key + ':' + accountId : '';
        const cooldownStorageKey = accountStorageKey(COOLDOWN_STORAGE_KEY);
        const limitKindStorageKey = accountStorageKey(LIMIT_KIND_STORAGE_KEY);
        const pendingStorageKey = accountStorageKey(PENDING_STORAGE_KEY);
        const fallbackStorageKey = accountStorageKey(FALLBACK_STORAGE_KEY);
        const reverseStorageKey = accountStorageKey(REVERSE_STORAGE_KEY);
        const phoneForm = document.getElementById('verify-phone-phone-form');
        const codeForm = document.getElementById('verify-phone-code-form');
        const phoneInput = document.getElementById('verify-phone-number');
        const codeInput = document.getElementById('verify-phone-code');
        const codeField = document.getElementById('verify-phone-code-field');
        const codeSlots = codeField
            ? Array.prototype.slice.call(codeField.querySelectorAll('[data-code-slot]'))
            : [];
        const sendButton = document.getElementById('verify-phone-send');
        const confirmButton = document.getElementById('verify-phone-confirm');
        const resendButton = document.getElementById('verify-phone-resend');
        const changeNumberButton = document.getElementById('verify-phone-change-number');
        const countryButton = document.getElementById('verify-phone-country-button');
        const countryPanel = document.getElementById('verify-phone-country-panel');
        const countryList = document.getElementById('verify-phone-country-list');
        const countrySearch = document.getElementById('verify-phone-country-search');
        const countryEmpty = document.getElementById('verify-phone-country-empty');
        const countryFlag = document.getElementById('verify-phone-country-flag');
        const countryDial = document.getElementById('verify-phone-country-dial');
        const countryWrapper = document.getElementById('verify-phone-country');
        const resendLabel = document.getElementById('verify-phone-resend-label');
        const resendDefaultLabel = resendLabel ? resendLabel.textContent : '';
        const sendSpinnerId = 'verify-phone-send-spinner';
        const confirmSpinnerId = 'verify-phone-confirm-spinner';
        const resendSpinnerId = 'verify-phone-resend-spinner';
        const errorBox = document.getElementById('verify-phone-error');
        const errorText = document.getElementById('verify-phone-error-text');
        const rateLimitedBox = document.getElementById('verify-phone-rate-limited');
        const rateLimitedText = document.getElementById('verify-phone-rate-limited-text');
        const budgetExhaustedBox = document.getElementById('verify-phone-budget-exhausted');
        const budgetExhaustedText = document.getElementById('verify-phone-budget-exhausted-text');
        const sendUncertainBox = document.getElementById('verify-phone-send-uncertain');
        const sendUncertainText = document.getElementById('verify-phone-send-uncertain-text');
        const sendStalledText = document.getElementById('verify-phone-send-stalled-text');
        const resendCooldownBox = document.getElementById('verify-phone-resend-cooldown');
        const unavailableBox = document.getElementById('verify-phone-unavailable');
        const successBox = document.getElementById('verify-phone-success');
        const providerWaivedBox = document.getElementById('verify-phone-provider-waived');
        const fallbackSentBox = document.getElementById('verify-phone-fallback-sent');
        const fallbackSentText = document.getElementById('verify-phone-fallback-sent-text');
        const fallbackSentHint = document.getElementById('verify-phone-fallback-sent-hint');
        const smsAlternativeButton = document.getElementById('verify-phone-sms-alternative');
        const smsAlternativeSpinnerId = 'verify-phone-sms-alternative-spinner';
        const statusLoadingBox = document.getElementById('verify-phone-status-loading');
        const fallbackBox = document.getElementById('verify-phone-fallback');
        const fallbackChannelsBox = document.getElementById('verify-phone-fallback-channels');
        const fallbackButtons = fallbackBox
            ? Array.prototype.slice.call(fallbackBox.querySelectorAll('[data-channel]'))
            : [];
        const fallbackChangeNumberButton = document.getElementById('verify-phone-fallback-change-number');
        const fallbackSpinnerId = 'verify-phone-fallback-spinner';
        const verifierRetryBox = document.getElementById('verify-phone-verifier-retry');
        const verifierRetryText = document.getElementById('verify-phone-verifier-retry-text');
        const verifierRetryButton = document.getElementById('verify-phone-verifier-retry-button');
        const reverseBox = document.getElementById('verify-phone-reverse');
        const reverseShowButton = document.getElementById('verify-phone-reverse-show');
        const stepWrapper = document.getElementById('verify-phone-step');
        const reverseOnlyCountries = parseJsonAttribute(root, 'data-reverse-only-countries');
        const paidLeadCountries = parseJsonAttribute(root, 'data-paid-lead-countries');
        // With no WhatsApp sender and no Telegram bot there is no destination to
        // send a token to, so the card can only ever answer `unavailable`.
        const reverseConfigured = root.getAttribute('data-reverse-configured') === '1';
        const paidAlternativeHeading = document.getElementById('verify-phone-paid-alternative-heading');

        if (!phoneForm || !codeForm || !phoneInput || !codeInput) return;
        setHidden(phoneForm, true);
        setHidden(codeForm, true);

        const state = {
            appCheckInitialized: false,
            auth: null,
            confirmationResult: null,
            confirmationUncertain: false,
            firebaseIdToken: '',
            firebaseRetryable: false,
            firebaseUser: null,
            nonce: '',
            recaptchaVerifier: null,
            recaptchaWidgetId: null,
            firebaseReadyPromise: null,
            firebaseReadyGeneration: 0,
            sessionGeneration: 0,
            sending: false,
            // A held send the reverse card leads. Only setSendStalled() writes it.
            sendStalled: false,
            confirming: false,
            country: null,
            // The number this session reserved. The fallback send has to name
            // the same one, and by then the phone form is hidden.
            phone: '',
            // Set once the fallback provider accepted a send, which is what
            // routes the confirm to the fallback endpoint.
            fallbackChannel: '',
            // The channels the server named for this number, in its own order.
            // The session answers first and the verifier-failure report answers
            // last, so the later answer replaces this one.
            channels: [],
            // The session's own list. Kept apart from `channels`, which an
            // offer narrows to the single channel it renders.
            sessionChannels: [],
            // The offer exactly as the server made it, unfiltered. `channels`
            // and `sessionChannels` hold the paid fallback pair alone, so
            // neither can answer whether the corridor carries a paid send.
            sessionOffer: null,
            // The number a corridor refusal answered for. Held as the number
            // rather than a flag, so it lapses when the user edits the field and
            // no clearing step can be missed.
            refusedPhone: '',
            // The lead sender the server picked for this session.
            route: '',
            // When the session was created, for the restore window.
            sessionStoredAt: 0,
            // Whether this session has already shown its WhatsApp lead. The
            // second send on one session is the SMS alternative.
            routeOffered: false,
            sendOwnership: null,
            sessionOwnership: null,
            fallbackDeadlineAt: 0,
            fallbackDeadlineMonotonic: 0,
            fallbackExpiryTimer: null,
            fallbackRequestUncertain: false,
            fallbackChannels: [],
            fallbackChannelsResolved: false,
            // Whether the verifier-failure answer took the page's own send
            // away. A refused corridor does that without opening the panel, so
            // the panel's visibility cannot stand in for it.
            verifierHeldSend: false,
            lifecycleGeneration: 0,
            pageActive: true,
            statusResolved: false,
        };

        let initialStatusRecoveryPending = true;
        setStatusLoading(true);

        const cooldown = createCooldownController({
            clearExpiry: () => {
                if (cooldownStorageKey) safeStorageRemove(cooldownStorageKey, SESSION_STORAGE);
                if (limitKindStorageKey) safeStorageRemove(limitKindStorageKey, SESSION_STORAGE);
            },
            clearInterval: window.clearInterval.bind(window),
            defaultLabel: resendDefaultLabel,
            defaultSeconds: RESEND_COOLDOWN_SECONDS,
            formatLabel: resendCooldownLabel,
            now: Date.now,
            onRestore: (remaining) => {
                hideMessages();
                const kind = safeStorageGet(limitKindStorageKey, SESSION_STORAGE);
                if (kind === BUDGET_EXHAUSTED || kind === RATE_LIMITED) {
                    renderLimit(kind, remaining);
                } else {
                    setHidden(resendCooldownBox, false);
                }
            },
            onTick: (remaining) => {
                const kind = safeStorageGet(limitKindStorageKey, SESSION_STORAGE);
                if (remaining > 0 && (kind === BUDGET_EXHAUSTED || kind === RATE_LIMITED)) {
                    renderLimit(kind, remaining);
                }
            },
            onExpire: () => {
                setHidden(rateLimitedBox, true);
                setHidden(budgetExhaustedBox, true);
            },
            readExpiry: () => cooldownStorageKey
                ? safeStorageGet(cooldownStorageKey, SESSION_STORAGE)
                : null,
            resendButton,
            resendLabel,
            sendButton,
            setInterval: window.setInterval.bind(window),
            writeExpiry: (expiresAt) => {
                if (cooldownStorageKey) {
                    safeStorageSet(cooldownStorageKey, String(expiresAt), SESSION_STORAGE);
                }
            },
        });
        cooldown.restore();

        // A session is reusable only while the server still holds it. The bound
        // is checked at every reuse, not once at load, because the page can sit
        // open far longer than the session lives.
        function retryableSessionIsLive() {
            const storedAt = state.sessionStoredAt;
            if (!storedAt) return false;
            const age = Date.now() - storedAt;

            return age >= 0 && age < RETRYABLE_SESSION_TTL_MS;
        }

        function rememberRetryableSession() {
            safeStorageSet(
                RETRYABLE_SESSION_STORAGE_KEY,
                JSON.stringify({
                    nonce: state.nonce,
                    phone: state.phone,
                    channels: state.sessionChannels,
                    sessionOffer: state.sessionOffer,
                    route: state.route,
                    storedAt: state.sessionStoredAt,
                }),
                SESSION_STORAGE,
            );
        }

        function forgetRetryableSession() {
            safeStorageRemove(RETRYABLE_SESSION_STORAGE_KEY, SESSION_STORAGE);
        }

        function restoreRetryableSession() {
            const raw = safeStorageGet(RETRYABLE_SESSION_STORAGE_KEY, SESSION_STORAGE);
            if (!raw) return;

            let stored = null;
            try {
                stored = JSON.parse(raw);
            } catch (e) {
                stored = null;
            }

            const storedAt = stored ? Number(stored.storedAt) : NaN;
            if (!stored || typeof stored.nonce !== 'string' || !stored.nonce || !isFinite(storedAt)) {
                forgetRetryableSession();
                return;
            }

            state.sessionStoredAt = storedAt;
            if (!retryableSessionIsLive()) {
                state.sessionStoredAt = 0;
                forgetRetryableSession();
                return;
            }

            state.nonce = stored.nonce;
            state.phone = typeof stored.phone === 'string' ? stored.phone : '';
            const storedChannels = Array.isArray(stored.channels) ? stored.channels : [];
            state.channels = storedChannels;
            state.sessionChannels = storedChannels.slice();
            state.sessionOffer = Array.isArray(stored.sessionOffer)
                ? stored.sessionOffer.slice()
                : storedChannels.slice();
            state.route = typeof stored.route === 'string' ? stored.route : '';
            // Not restored. The offer panel does not survive a reload, so a
            // WhatsApp-route session re-offers its lead rather than dropping the
            // caller onto SMS with no WhatsApp action on screen.
            state.routeOffered = false;
            state.firebaseRetryable = true;
        }

        restoreRetryableSession();

        const hasCountryPicker = Boolean(
            countryButton && countryPanel && countryList && countrySearch && countryEmpty
                && countryDial && countryFlag && countryWrapper
        );

        // The corridor decides whether the page offers a paid send, and the
        // reverse panel is not the authority on it. The panel reads its corridor
        // only while it renders, and several paths put the phone form back on
        // screen without rendering it.
        function corridorWithdrawsPaidSend() {
            // The server already answered for this number, and its answer
            // outranks the country the picker shows.
            if (matchesSessionPhone(state.refusedPhone, currentPhone())) return true;

            return isReverseOnlyCountry(
                state.country ? state.country.iso : '',
                reverseOnlyCountries,
            );
        }

        // `restorePhoneForm()` is the only path that reveals this form, and
        // `showCodeForm()` is what hides it, so the form cannot lag the step the
        // user looks at. A code step holds a paid code already, and its SMS
        // alternative answers for that send rather than starting a new one.
        function onPhoneStep() {
            return phoneForm !== null && !phoneForm.classList.contains('hidden');
        }

        function paidSendWithdrawn() {
            return onPhoneStep() && corridorWithdrawsPaidSend();
        }

        // The single writer of the send button's visibility. It reads the number
        // and the picked country, so every control that rewrites either one of
        // them calls it through `onNumberRewritten()`.
        function applyPaidSendWithdrawal() {
            setHidden(sendButton, corridorWithdrawsPaidSend());
        }

        // The dial field is sized by its `size` attribute rather than a width
        // class, so each country code gets exactly the room it needs and the
        // national number always starts right after it.
        function resizeDialInput() {
            countryDial.size = Math.max(2, countryDial.value.length);
        }

        function applyCountry(country, syncDialInput) {
            if (!country) return;

            state.country = country;
            countryFlag.src = FLAG_BASE + (country.flag || country.iso) + '.svg';
            countryFlag.alt = country.name;

            if (syncDialInput !== false) {
                countryDial.value = '+' + country.dial;
                resizeDialInput();
            }

            // Picking from the list rewrites the dial code, so the number a
            // standing offer was made for is no longer the one on screen.
            onNumberRewritten();
            reverse.refreshCorridor();
        }

        // The picker and the typed dial code are two views of one value, so a typed
        // prefix moves the flag without rewriting what is being typed.
        function syncCountryFromDialInput() {
            const country = countryForDial(digitsOnly(countryDial.value));
            if (country && (!state.country || country.iso !== state.country.iso)) {
                applyCountry(country, false);
            }
        }

        function countryOption(country, onSelect) {
            const item = document.createElement('li');
            const button = document.createElement('button');
            button.type = 'button';
            button.id = 'verify-phone-country-option-' + country.iso;
            button.setAttribute('role', 'option');
            button.setAttribute(
                'aria-selected',
                state.country && state.country.iso === country.iso ? 'true' : 'false',
            );
            button.tabIndex = -1;
            button.className = 'flex min-h-[44px] w-full touch-manipulation items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-sky-50 dark:text-gray-200 dark:hover:bg-sky-950/40';

            const flag = document.createElement('img');
            flag.src = FLAG_BASE + (country.flag || country.iso) + '.svg';
            flag.alt = '';
            flag.loading = 'lazy';
            flag.className = 'h-5 w-5 flex-shrink-0 rounded-full object-cover';

            const name = document.createElement('span');
            name.className = 'min-w-0 flex-1 truncate';
            name.textContent = country.name;

            const dial = document.createElement('span');
            dial.className = 'flex-shrink-0 text-gray-500 dark:text-gray-400';
            dial.textContent = '+' + country.dial;

            button.append(flag, name, dial);
            button.addEventListener('click', onSelect);

            item.appendChild(button);

            return item;
        }

        // Both halves are normalized by the shared composer. The edited dial is
        // authoritative; matching country metadata supplies national-prefix rules.
        function composeE164() {
            const dial = digitsOnly(countryDial && countryDial.value);
            const selectedCountry = state.country && state.country.dial === dial
                ? state.country
                : countryForDial(dial);

            return composePhoneNumber(
                selectedCountry,
                phoneInput.value,
                dial,
            );
        }

        // A number copied from a contact list carries its own country code, as
        // '+61...' or as the '0061...' international prefix. Left alone it gets
        // appended to whatever country is selected and composes a malformed
        // '+1 61...', so it is split across the two fields instead.
        //
        // Without a leading '+' the intent is ambiguous, and guessing on every
        // keystroke would yank the country out from under someone mid-type. So
        // the unmarked form is only reinterpreted on paste, and only when the
        // digits cannot be a national number for the selected country: NANP
        // (+1, our default) is exactly 10 digits, and E.164 caps the whole
        // number at 15.
        function absorbInternationalNumber(isPaste) {
            const parsed = splitInternationalNumber(
                phoneInput.value,
                countryDial.value,
                isPaste,
                countryForDial,
            );
            if (!parsed) return;

            applyCountry(parsed.country);
            phoneInput.value = parsed.nationalNumber;
        }

        function setResendBusy(busy) {
            // While a resend cooldown counts down, the button stays disabled, so a
            // finishing send (busy=false) must not re-enable it. Only the cooldown
            // ending re-enables it.
            cooldown.setResendBusy(busy);

            const resendSpinner = document.getElementById(resendSpinnerId);
            if (resendSpinner) {
                resendSpinner.classList.toggle('animate-spin', busy);
            }
        }

        function resendCooldownLabel(seconds) {
            if (window.trans) {
                return window.trans('auth', 'phone_verify_resend_in', { seconds: seconds });
            }

            return 'Resend in ' + seconds + 's';
        }

        function startResendCooldown(seconds) {
            if (limitKindStorageKey) safeStorageRemove(limitKindStorageKey, SESSION_STORAGE);
            cooldown.start(seconds);
        }

        function changeNumber() {
            if (state.sending || state.confirming) return;

            invalidatePendingWork();
            // Return to the phone step. Drop any pending confirmation and nonce so a
            // stale Firebase session can't confirm against the previous number.
            state.sessionGeneration += 1;
            state.confirmationResult = null;
            state.firebaseIdToken = '';
            state.firebaseUser = null;
            state.nonce = '';
            state.fallbackChannel = '';
            state.sessionChannels = [];
            state.firebaseRetryable = false;
            state.verifierHeldSend = false;
            forgetRetryableSession();
            cooldown.stop();
            cooldown.setIdleLabel(resendDefaultLabel);
            setHidden(changeNumberButton, false);
            setSmsAlternativeHidden(true);
            closeFallbackOffer();
            state.fallbackRequestUncertain = false;
            state.fallbackChannels = [];
            state.fallbackChannelsResolved = false;
            clearFallbackExpiry();
            clearPendingLock();
            clearFallbackLock();
            cooldown.stop();
            cooldown.setIdleLabel(resendDefaultLabel);
            setHidden(changeNumberButton, false);
            closeFallbackOffer();
            fallbackButtons.forEach(function (button) {
                button.disabled = true;
                setHidden(button, true);
            });
            setFallbackBusy(false);
            setHidden(resendButton, false);
            hideMessages();
            codeInput.value = '';
            renderCodeSlots();
            setHidden(codeForm, true);
            // The reverse token answers for the number this replaces, so it
            // goes with the number rather than following it to the next one.
            reverse.dropChallenge();
            restorePhoneForm();
            if (reverseConfigured) reverse.start();
            phoneInput.focus();
        }

        // The number the field holds. A send names this, and a session that
        // reserved a different one is refused rather than sent to the old one.
        function currentPhone() {
            return hasCountryPicker ? composeE164() : phoneInput.value.trim();
        }

        function setPhoneEditBusy(busy) {
            setControlsDisabled([
                phoneInput,
                countryButton,
                countryDial,
                countrySearch,
                fallbackChangeNumberButton,
            ], busy);
        }

        /**
         * A send that outlived its deadline may never finish, and the page holds
         * it until the session ends. The session is live, so the reverse card
         * joins it at no cost, and it leads the phone step while the hold lasts.
         */
        function setSendStalled(stalled) {
            const leads = stalled && reverseConfigured;
            setHidden(sendUncertainText, leads);
            setHidden(sendStalledText, !leads);
            if (reverseShowButton) {
                if (leads) reverseShowButton.setAttribute('aria-describedby', 'verify-phone-send-stalled-text');
                else reverseShowButton.removeAttribute('aria-describedby');
            }
            const changed = state.sendStalled !== leads;
            state.sendStalled = leads;
            if (!leads) {
                if (changed) reverse.refreshCorridor();
                return;
            }
            // Recovery re-reads a restored lock on every poll, and a repeat must
            // not pull the focus back. A back-forward cache return hides the card
            // under a stall that stands, so that repeat shows it again.
            if (!changed && reverse.state.visible) return;

            // A tap before the deadline met the held send and was refused.
            reverse.clearRefusal();
            reverse.start();
            if (reverse.state.phase === 'offer' && reverseShowButton) reverseShowButton.focus();
        }

        function hideMessages() {
            setHidden(errorBox, true);
            setHidden(rateLimitedBox, true);
            setHidden(budgetExhaustedBox, true);
            setHidden(sendUncertainBox, true);
            // The tap names the stalled line only while that line is on screen.
            if (reverseShowButton) reverseShowButton.removeAttribute('aria-describedby');
            setHidden(resendCooldownBox, true);
            setHidden(unavailableBox, true);
            setHidden(successBox, true);
            setHidden(providerWaivedBox, true);
            setHidden(fallbackSentBox, true);
            setHidden(verifierRetryBox, true);
        }

        function setStatusLoading(active) {
            setHidden(statusLoadingBox, !active);
            if (root) root.setAttribute('aria-busy', active ? 'true' : 'false');
        }

        function finishInitialStatusRecovery() {
            if (!initialStatusRecoveryPending) return;
            initialStatusRecoveryPending = false;
            setStatusLoading(false);
        }

        function showError(messageKey) {
            if (errorText) {
                errorText.textContent = messages[messageKey] || messages.generic || errorText.textContent;
            }

            hideMessages();
            setHidden(errorBox, false);
            focusVerificationError(messageKey, phoneInput, codeInput);
        }

        function showRateLimited() {
            hideMessages();
            setHidden(rateLimitedBox, false);
        }

        function renderLimit(kind, seconds) {
            const budget = kind === BUDGET_EXHAUSTED;
            const text = budget ? budgetExhaustedText : rateLimitedText;
            const messageKey = budget ? 'budget_exhausted_retry' : 'rate_limited_retry';
            if (text) {
                const duration = formatPhoneRetryDuration(seconds || 0, formatDurationShort);
                text.textContent = String(messages[messageKey] || '').replace(
                    '{time}',
                    duration,
                );
            }
            setHidden(budget ? budgetExhaustedBox : rateLimitedBox, false);
        }

        function showLimit(kind, seconds) {
            cooldown.start(seconds);
            if (limitKindStorageKey) safeStorageSet(limitKindStorageKey, kind, SESSION_STORAGE);
            hideMessages();
            renderLimit(kind, seconds);
        }

        function showStaticLimit(kind) {
            hideMessages();
            setHidden(kind === BUDGET_EXHAUSTED ? budgetExhaustedBox : rateLimitedBox, false);
        }

        function showResendCooldown(seconds) {
            hideMessages();
            setHidden(resendCooldownBox, false);
            startResendCooldown(seconds);
        }

        // The corridor the page is about to dial. The country picker holds it
        // before any session reserves a number, which is what lets a
        // pre-session report be answered with the channels that corridor has.
        function selectedCountryIso() {
            return state.country ? state.country.iso.toUpperCase() : '';
        }

        // Same-origin diagnostics remain reachable when content blockers disable Sentry.
        function reportVerifierFailure(error, sending) {
            const values = verifierFailureReport(error, sending);
            if (!values) return;

            // A report with no nonce names no challenge the server can look up,
            // so it reports the corridor and is answered from that alone.
            // client_timeout takes this route inside a session too: the timeout
            // is the client's own environment, and the page may hold no nonce
            // by the time it fires.
            if (values.reason === 'client_timeout' || !state.nonce) {
                if (
                    values.reason !== 'client_timeout'
                    && values.reason !== BLOCKED
                    && values.reason !== UNCONFIGURED
                ) {
                    return;
                }

                // No gate row names the corridor before a session exists, so
                // the picker is the only source of it for this report.
                const country = selectedCountryIso();
                if (country) values.phone_country = country;
                sendReport(post('/phone-verification/verifier-failure-diagnostic', values));

                return;
            }

            values.nonce = state.nonce;
            answerReport(post('/phone-verification/verifier-failure', values));
        }

        // The diagnostic endpoint answers 204, so this report changes no screen.
        // It carries the corridor, which is where the failure is counted.
        function sendReport(request) {
            try {
                request.catch(function () {
                    // Reporting is best-effort and never surfaces to the user.
                });
            } catch (e) {
                // Same.
            }
        }

        // The report is also how the page learns what to do next: a second
        // provider that can reach this number, or a gate the server already
        // cleared. Both reports answer in one shape, so one reader serves both.
        // A session that moved on while the request was in flight answers for a
        // challenge the page no longer holds, so that answer is dropped.
        function answerReport(request) {
            const generation = state.sessionGeneration;

            try {
                request.then(async function (response) {
                    if (!response || !response.ok) return;
                    if (state.sessionGeneration !== generation) return;
                    const body = await responseJson(response);
                    if (state.sessionGeneration !== generation) return;
                    if (!body) return;
                    answerVerifierFailure(body);
                }).catch(function () {
                    // Reporting is best-effort and never surfaces to the user.
                });
            } catch (e) {
                // Same.
            }
        }

        // The report is the page's only source of a next step once the verifier
        // refuses, so every branch here leaves one on screen.
        function answerVerifierFailure(body) {
            const channels = Array.isArray(body.fallback_channels) ? body.fallback_channels : [];
            const retryable = body.verifier_retryable === true;

            if (
                offeredChannels(channels, FALLBACK_CHANNELS).length
                && offerFallback(channels, retryable)
            ) {
                return;
            }

            setFallbackChannels([]);

            // A reverse channel is taken on the card, never on a channel button.
            // It costs nothing and no provider limit reaches it, so it opens
            // beside a countdown as well as beside the retry panel.
            if (reverseConfigured && offeredChannels(channels, REVERSE_FALLBACK_CHANNELS).length) {
                reverse.start();
            }

            // A corridor that withdraws the page's paid send leaves no verifier
            // to retry. The card's own position is a separate question.
            const cardStands = reverseBox !== null && !reverseBox.classList.contains('hidden');

            // One predicate answers for both call sites. A refused offer is
            // recorded against the number it answered for, so the withdrawal
            // already carries the session answer that this retry would re-read.
            const paidRetryServes = !paidSendWithdrawn();

            showVerifierRetry(retryable && paidRetryServes, cardStands);
        }

        // The plain error box names no action, so the retry panel replaces it.
        // A countdown and a lockout each name one already, and overwriting them
        // would invite a send the provider refuses, so they keep the screen and
        // the cooldown keeps the send button.
        function showVerifierRetry(canRetry, cardStands) {
            if (!verifierRetryBox || !errorBox || errorBox.classList.contains('hidden')) return;

            // A refused corridor answers every retry the same way, so the held
            // session and the page's own send go with the retry they belong to.
            // Implicit submission reaches the send even though the disabled
            // button does not, so sendCode() reads the same flag through
            // holdsTheField().
            state.firebaseRetryable = canRetry;
            if (!canRetry) forgetRetryableSession();
            state.verifierHeldSend = !canRetry;
            if (sendButton) sendButton.disabled = !canRetry;

            // The card is the route here. A panel with no retry to add would
            // only deny a route the user has.
            if (cardStands && !canRetry) return;

            setHidden(errorBox, true);
            if (verifierRetryText) {
                verifierRetryText.textContent = messages[
                    canRetry ? 'verifier_failed_retryable' : 'verifier_failed_blocked'
                ] || verifierRetryText.textContent;
            }
            if (verifierRetryButton) {
                verifierRetryButton.disabled = false;
                verifierRetryButton.classList.toggle('flex', canRetry);
                setHidden(verifierRetryButton, !canRetry);
            }
            setHidden(verifierRetryBox, false);
            window.LucideIcons?.initialize(verifierRetryBox);
            if (canRetry && verifierRetryButton) verifierRetryButton.focus();
        }

        // The panel stands for one number. Taking it down restores the page's
        // own send, which a refused corridor disabled. The refusal reaches a
        // reverse-only corridor without opening the panel, so the restore
        // follows the flag rather than the panel.
        function closeVerifierRetry() {
            setHidden(verifierRetryBox, true);
            if (!state.verifierHeldSend) return;

            state.verifierHeldSend = false;
            if (sendButton) sendButton.disabled = false;
        }

        // The verifier is built once and answers every later send from cache, so
        // dropping it is what makes another attempt a fresh check.
        function retryVerifier() {
            if (state.sending || state.confirming || reverse.busy()) return;

            closeVerifierRetry();
            invalidateFirebaseReadiness();
            clearRecaptchaVerifier();
            sendCode(false);
        }

        // Render the server's ordered channel list with the primary action first.
        function renderFallbackChannels(channels) {
            if (!fallbackChannelsBox) return 0;

            const available = offeredChannels(channels, FALLBACK_CHANNELS);
            state.fallbackChannels = available;
            state.channels = available;
            state.fallbackChannelsResolved = true;
            channels = available;

            // Counts buttons actually shown, not names offered. An id that
            // drifts from the template leaves its channel unrendered, and
            // returning the offered count would open an empty panel.
            let rendered = 0;

            channels.forEach(function (channel, index) {
                const button = document.getElementById(FALLBACK_CHANNEL_BUTTON_IDS[channel]);
                if (!button) return;

                rendered++;

                const primary = index === 0;
                FALLBACK_PRIMARY_CLASSES.concat(FALLBACK_SECONDARY_CLASSES).forEach(function (name) {
                    button.classList.remove(name);
                });
                (primary ? FALLBACK_PRIMARY_CLASSES : FALLBACK_SECONDARY_CLASSES).forEach(function (name) {
                    button.classList.add(name);
                });

                const label = button.querySelector('[data-channel-label]');
                const keys = FALLBACK_LABEL_KEYS[channel];
                if (label && keys) {
                    const text = messages[primary ? keys.primary : keys.secondary];
                    if (text) label.textContent = text;
                }

                button.classList.add('flex');
                setHidden(button, false);
                // The server's order is the offer order, so the DOM follows it.
                fallbackChannelsBox.appendChild(button);
            });

            fallbackButtons.forEach(function (button) {
                if (channels.indexOf(button.getAttribute('data-channel')) !== -1) return;

                button.classList.remove('flex');
                setHidden(button, true);
            });

            return rendered;
        }

        // Renders the channel list without opening the offer, so a recovered
        // session can restore its choices before it decides to show them.
        function setFallbackChannels(channels) {
            renderFallbackChannels(channels);
        }

        // Returns whether the panel took the screen. A caller that has no other
        // offer answers a false with a next step of its own.
        function offerFallback(channels, verifierRetryable) {
            if (!fallbackBox || !state.nonce || !matchesSessionPhone(state.phone, currentPhone())) return false;

            const offered = offeredChannels(channels, FALLBACK_CHANNELS);
            if (offered.length) state.channels = offered;
            // A recovered session that never learned its channel list has
            // nothing to offer, and an empty panel reads as "no way forward".
            if (!offered.length && !state.fallbackChannelsResolved) return false;
            if (!renderFallbackChannels(state.channels)) return false;

            hideMessages();
            setHidden(fallbackBox, false);
            // Both panels name a WhatsApp action, and they run in opposite
            // directions. Only the paid offer is on screen while it stands.
            reverse.hide();
            state.firebaseRetryable = verifierRetryable === true;
            // Firebase already refused this corridor, so the session cannot be
            // spent on it after a reload either.
            if (!state.firebaseRetryable) forgetRetryableSession();
            // The page's own send button is the Firebase SMS route, and it is
            // the only SMS action on screen whenever the panel offers WhatsApp
            // alone. It stays live while the server reports the verifier as
            // retryable, so a user with no WhatsApp is never left without one.
            // A provider refusal is the other case: Firebase already refused
            // this corridor, so a tap would spend a session and a reservation
            // on a route that cannot deliver, and the disabled: utilities
            // demote the button so one primary action is on screen.
            if (sendButton) sendButton.disabled = verifierRetryable !== true;
            setFallbackBusy(false);
            window.LucideIcons?.initialize(fallbackBox);
            // The panel takes focus, then the first action it can offer. With
            // every action disabled the panel itself keeps it.
            fallbackBox.focus();
            const firstAction = fallbackButtons.find(function (button) {
                return !button.disabled;
            });
            if (firstAction) firstAction.focus();

            return true;
        }

        // The offer belongs to the number the session reserved. Editing the
        // field withdraws it, and the page's send button comes back so a new
        // number can open a new session.
        function closeFallbackOffer() {
            setHidden(fallbackBox, true);
            if (sendButton) sendButton.disabled = false;
        }

        // Called from every control that rewrites the number.
        function onNumberRewritten() {
            // The withdrawal reads the number, so every rewrite re-renders it.
            applyPaidSendWithdrawal();

            if (matchesSessionPhone(state.phone, currentPhone())) return;

            // The retry panel answers one failed attempt on one number, so a
            // rewrite takes it down with the offer beside it.
            closeVerifierRetry();

            if (fallbackBox === null || fallbackBox.classList.contains('hidden')) return;

            closeFallbackOffer();
            if (reverseConfigured) reverse.start();
        }

        // A panel holds the field while it stands for the number the field
        // still carries. Editing the number hands the field back.
        function holdsTheField(box) {
            return box !== null
                && !box.classList.contains('hidden')
                && matchesSessionPhone(state.phone, currentPhone());
        }

        // The button is declared hidden, so showing it has to restore the
        // display utility as well. Same handling the offer panel gives its own
        // channel buttons.
        function setSmsAlternativeHidden(hidden) {
            if (!smsAlternativeButton) return;

            smsAlternativeButton.classList.toggle('flex', !hidden);
            setHidden(smsAlternativeButton, hidden);
        }

        // Whether the code step still owes the caller an SMS route, decided on
        // the session's own channel list rather than the channel just sent.
        function smsAlternativeAvailable(sentChannel) {
            return Boolean(smsAlternativeButton) && hasSmsAlternative({
                route: state.route,
                sessionChannels: state.sessionChannels,
                sentChannel: sentChannel,
                verifierRetryable: state.firebaseRetryable,
            });
        }

        // The second line answers the user who never sees the message, so it
        // names the SMS button while that button is on screen. Telling them to
        // start again there would spend a session for a route already offered.
        function showFallbackSent(channel, smsAlternative) {
            const text = messages[FALLBACK_SENT_KEYS[channel]];
            if (fallbackSentText && text) {
                fallbackSentText.textContent = text;
            }

            const hint = smsAlternative
                ? messages.fallback_not_received_sms
                : messages.fallback_not_received;
            if (fallbackSentHint && hint) {
                fallbackSentHint.textContent = hint;
            }

            setHidden(fallbackSentBox, false);
        }

        function setFallbackBusy(busy) {
            fallbackButtons.forEach(function (button) {
                button.disabled = busy || button.classList.contains('hidden');
            });
            setHidden(document.getElementById(fallbackSpinnerId), !busy);
        }

        function sendFallback(channel) {
            if (state.sending || state.confirming || reverse.busy() || !state.nonce
                || !matchesSessionPhone(state.phone, currentPhone())) return;

            const remainingMs = state.fallbackDeadlineMonotonic - monotonicNow();
            if (remainingMs <= 0) {
                showUnavailable();
                return;
            }

            state.sending = true;
            const generation = state.sessionGeneration;
            setFallbackBusy(true);
            setPhoneEditBusy(true);
            hideMessages();

            const requestLifecycleGeneration = state.lifecycleGeneration;
            const requestSessionGeneration = state.sessionGeneration;
            const ownsRequest = () => state.pageActive
                && state.lifecycleGeneration === requestLifecycleGeneration
                && state.sessionGeneration === requestSessionGeneration;
            const request = (async function () {
                const response = await post('/phone-verification/fallback', {
                    nonce: state.nonce,
                    phone: state.phone,
                    channel: channel,
                });
                const data = await responseJson(response);
                if (
                    !ownsRequest()
                ) {
                    throw codedError(BLOCKED);
                }
                return { response: response, data: data };
            })();

            sendRecovery.own({
                durationMs: remainingMs,
                onExpire() {
                    if (!ownsRequest()) return;
                    state.sending = false;
                    setFallbackBusy(false);
                    setPhoneEditBusy(false);
                    showUnavailable();
                },
                onReject(error) {
                    if (!ownsRequest()) return;
                    showFailure(error, true);
                    state.sending = false;
                    setFallbackBusy(false);
                    setPhoneEditBusy(false);
                },
                onResolve(result, settlement) {
                    if (!ownsRequest()) return;
                    recovery.clear();
                    const response = result.response;
                    const data = result.data;

                    if (!response.ok) {
                        const extra = data && data.error && data.error.extra;
                        const retryable = extra && extra.retryable;
                        if (retryable === true && extra.session_consumed === true) {
                            resetFreshPhoneSession();
                            return;
                        }
                        if (retryable === false || (retryable === true && extra.session_consumed !== true)) {
                            state.fallbackRequestUncertain = true;
                            persistPendingLock(settlement.remainingMs);
                            hideMessages();
                            setHidden(fallbackBox, true);
                            setHidden(sendUncertainBox, false);
                            setHidden(document.getElementById(fallbackSpinnerId), true);
                            recovery.start();
                            return;
                        }
                        state.fallbackRequestUncertain = false;
                        const failure = apiFailure(
                            response,
                            data,
                            'session_failed',
                            RESEND_COOLDOWN_SECONDS,
                        );
                        showFailure(codedError(failure.code, failure.retryAfter), true);
                    } else if (data.waived) {
                        completeWithProviderWaiver();
                    } else if (data.sent !== true) {
                        showUnavailable();
                    } else {
                        state.fallbackChannel = channel;
                        // Clears the stored session only, so a reload starts
                        // clean. The in-memory nonce stays reusable for
                        // RETRYABLE_SESSION_TTL_MS, which is what lets the SMS
                        // alternative reach Firebase without buying a second
                        // session. Clearing state.firebaseRetryable or
                        // state.sessionStoredAt here makes that button mint one.
                        forgetRetryableSession();
                        closeFallbackOffer();
                        armFallbackExpiry(remainingMs);
                        if (fallbackStorageKey && state.fallbackDeadlineAt > Date.now()) {
                            safeStorageSet(
                                fallbackStorageKey,
                                String(state.fallbackDeadlineAt),
                                SESSION_STORAGE,
                            );
                        }
                        setHidden(fallbackBox, true);
                        codeInput.value = '';
                        // A fallback resend needs a new session, so the control
                        // that asked for another code starts the flow over.
                        cooldown.stop();
                        cooldown.setIdleLabel(messages.start_again || resendDefaultLabel);
                        showCodeForm();
                        // A provider that accepts a send can still fail it on
                        // its own webhook, so the session's SMS route and the
                        // change-number control stay on the code step.
                        const smsAlternative = smsAlternativeAvailable(channel);
                        setSmsAlternativeHidden(!smsAlternative);
                        setHidden(changeNumberButton, !smsAlternative);
                        showFallbackSent(channel, smsAlternative);
                    }

                    state.sending = false;
                    setFallbackBusy(false);
                    setPhoneEditBusy(false);
                },
                onTimeout() {
                    if (!ownsRequest()) return;
                    state.fallbackRequestUncertain = true;
                    const error = codedError('client_timeout');
                    error.message = 'fallback';
                    reportVerifierFailure(error, true);
                    hideMessages();
                    setHidden(fallbackBox, true);
                    setHidden(sendUncertainBox, false);
                    setHidden(document.getElementById(fallbackSpinnerId), true);
                    recovery.start();
                },
                promise: request,
                retainTimedOutRejection: true,
                timeoutMs: PHASE_TIMEOUT_MS,
            });
        }

        // Each failure the script can tell apart says what actually went wrong,
        // because "try again" is useless advice for a bad number or a stale code.
        function showFailure(error, sending) {
            const code = error && error.code;

            if (!error || !error.reported) reportVerifierFailure(error, sending);

            // A send refused for being too soon is not a failure the user has to
            // do anything about, so it restarts the countdown they raced instead
            // of showing lockout copy. The button re-disables and the label ticks
            // down, which is the same feedback a successful send gives.
            if (code === RESEND_COOLDOWN) {
                showResendCooldown(error && error.retryAfter);
                return;
            }

            if (code === RATE_LIMITED || code === FIREBASE_RATE_LIMITED) {
                // Every server refusal names its wait. Firebase's own throttle
                // names none, and the static copy is what answers that case.
                if (error && error.retryAfter) {
                    showLimit(RATE_LIMITED, error.retryAfter);
                } else {
                    showRateLimited();
                }
                return;
            }

            if (code === BUDGET_EXHAUSTED) {
                if (error && error.retryAfter) showLimit(BUDGET_EXHAUSTED, error.retryAfter);
                else showStaticLimit(BUDGET_EXHAUSTED);
                return;
            }

            if (code === FIREBASE_QUOTA_EXCEEDED || code === UNCONFIGURED) {
                showUnavailable();
                return;
            }

            // A library that never loaded and a token the server refused are the
            // same story to the user: something on their side blocked the check.
            if (code === BLOCKED) {
                showError('security');
                return;
            }

            // The undeliverable copy tells the user to try another number,
            // which is wrong advice once a code has reached the one they gave.
            if (!sending && FIREBASE_MESSAGE_KEYS[code] === 'undeliverable') {
                showError('generic');
                return;
            }

            showError(FIREBASE_MESSAGE_KEYS[code]);
        }

        function showUnavailable() {
            hideMessages();
            setHidden(unavailableBox, false);
        }

        function clearFallbackExpiry() {
            if (state.fallbackExpiryTimer !== null) {
                window.clearTimeout(state.fallbackExpiryTimer);
                state.fallbackExpiryTimer = null;
            }
        }

        function expireFallbackSession() {
            invalidatePendingWork();
            clearFallbackExpiry();
            clearFallbackLock();
            state.sessionGeneration += 1;
            state.nonce = '';
            state.fallbackChannel = '';
            state.fallbackDeadlineAt = 0;
            state.fallbackDeadlineMonotonic = 0;
            setHidden(codeForm, true);
            unlockSend();
            restorePhoneForm();
            showUnavailable();
        }

        function armFallbackExpiry(durationMs) {
            clearFallbackExpiry();
            state.fallbackExpiryTimer = window.setTimeout(expireFallbackSession, durationMs);
        }

        function showSuccess() {
            hideMessages();
            setHidden(successBox, false);
        }

        function showProviderWaived() {
            hideMessages();
            setHidden(providerWaivedBox, false);
        }

        function completeVerification() {
            invalidatePendingWork();
            clearFallbackExpiry();
            clearPendingLock();
            clearFallbackLock();
            state.firebaseIdToken = '';
            state.firebaseUser = null;
            state.confirmationResult = null;
            state.confirmationUncertain = false;
            state.nonce = '';
            state.fallbackChannel = '';
            state.firebaseRetryable = false;
            forgetRetryableSession();
            closeFallbackOffer();
            reverse.stop();
            cooldown.stop();
            // The redirect takes a moment. Hide the form so the notice stands alone.
            setHidden(codeForm, true);
            showSuccess();
            window.location.assign(redirectUrl || ACCOUNT_URL);
        }

        // The gate is cleared, so the user goes where a verified user goes. The
        // send failed, so the page can be on either form when this runs.
        function completeWithProviderWaiver() {
            invalidatePendingWork();
            clearFallbackExpiry();
            clearPendingLock();
            clearFallbackLock();
            state.firebaseIdToken = '';
            state.firebaseUser = null;
            state.confirmationResult = null;
            state.confirmationUncertain = false;
            state.nonce = '';
            state.fallbackChannel = '';
            state.firebaseRetryable = false;
            forgetRetryableSession();
            reverse.stop();
            cooldown.stop();
            closeFallbackOffer();
            setHidden(phoneForm, true);
            setHidden(codeForm, true);
            showProviderWaived();
            window.location.assign(redirectUrl || ACCOUNT_URL);
        }

        // Each box mirrors one character of the single underlying input. The
        // strings are written out in full because Tailwind's JIT scans this file
        // for literals, so a class assembled from fragments would never be
        // generated.
        const CODE_SLOT_BASE = 'flex h-14 flex-1 items-center justify-center rounded-xl border-2 text-xl font-semibold transition-colors duration-200 ';
        const CODE_SLOT_EMPTY = 'border-gray-200 bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';
        const CODE_SLOT_FILLED = 'border-sky-500 bg-white text-gray-900 dark:border-sky-400 dark:bg-gray-800 dark:text-gray-100';
        const CODE_SLOT_ACTIVE = 'border-sky-500 bg-sky-50 text-gray-900 ring-2 ring-sky-500/20 dark:border-sky-400 dark:bg-sky-950/30 dark:text-gray-100';

        function renderCodeSlots() {
            if (!codeSlots.length) return;

            const value = digitsOnly(codeInput.value).slice(0, CODE_LENGTH);
            const focused = document.activeElement === codeInput;
            // The box the next character lands in, so an incomplete code shows
            // where the user is.
            const caret = value.length;

            codeSlots.forEach(function (slot, index) {
                slot.textContent = value.charAt(index);

                let state = CODE_SLOT_EMPTY;
                if (index < value.length) {
                    state = CODE_SLOT_FILLED;
                }
                if (focused && index === caret) {
                    state = CODE_SLOT_ACTIVE;
                }

                slot.className = CODE_SLOT_BASE + state;
            });
        }

        // Typing always appends, so the caret is pinned to the end. Without this
        // a tap in the middle of the row would drop the next digit into the
        // middle of the code.
        function pinCodeCaret() {
            const end = codeInput.value.length;
            try {
                codeInput.setSelectionRange(end, end);
            } catch (e) {
                // Not supported on this input type in some browsers.
            }
        }

        // Emptied after a rejected code so the boxes are ready for a fresh
        // attempt. Leaving the failed digits in place would also leave the
        // field complete, so the auto-submit could never fire again.
        function resetCodeInput() {
            codeInput.value = '';
            renderCodeSlots();
            codeInput.focus();
        }

        // Every path that puts the phone form back on screen goes through here,
        // so the card's step never lags behind the form the user is looking at.
        function restorePhoneForm() {
            setHidden(phoneForm, false);
            reverse.setStep('phone');
        }

        function showCodeForm() {
            // A user whose code never arrives is on this step, so the free route
            // stays on screen as the alternate below the code field.
            if (reverseConfigured) reverse.startOnCodeStep();
            setHidden(sendUncertainBox, true);
            setHidden(phoneForm, true);
            setHidden(codeForm, false);
            renderCodeSlots();
            codeInput.focus();
        }

        // The Firebase SDK hardcodes www.google.com for its reCAPTCHA script,
        // which is unreachable from mainland China and from networks that block
        // Google. It does reuse an already-present window.grecaptcha that exposes
        // render(), so loading Google's own recaptcha.net mirror first gives those
        // users a working verifier. Same keys, same tokens.
        function loadRecaptchaLibrary() {
            if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
                return Promise.resolve(true);
            }

            return new Promise(function (resolve) {
                let settled = false;

                function settle(loaded) {
                    if (settled) return;
                    settled = true;
                    window.clearTimeout(timer);
                    delete window[RECAPTCHA_READY_CALLBACK];
                    // A false result is not fatal: the SDK falls back to loading
                    // its own script from www.google.com.
                    resolve(loaded && !!(window.grecaptcha && window.grecaptcha.render));
                }

                const timer = window.setTimeout(function () {
                    settle(false);
                }, RECAPTCHA_LOAD_TIMEOUT_MS);

                // Defined before the script is appended, so a cached script that
                // executes immediately still finds its callback.
                window[RECAPTCHA_READY_CALLBACK] = function () {
                    settle(true);
                };

                const script = document.createElement('script');
                script.src = RECAPTCHA_MIRROR_SRC;
                script.async = true;
                script.defer = true;
                script.onerror = function () {
                    settle(false);
                };

                document.head.appendChild(script);
            });
        }

        async function ensureFirebase() {
            if (state.auth && state.recaptchaVerifier) return state.auth;
            if (state.firebaseReadyPromise) return state.firebaseReadyPromise;

            const generation = state.firebaseReadyGeneration;
            const readiness = initializeFirebase(generation);
            state.firebaseReadyPromise = readiness;
            try {
                return await readiness;
            } catch (error) {
                if (state.firebaseReadyPromise === readiness) state.firebaseReadyPromise = null;
                throw error;
            }
        }

        function invalidateFirebaseReadiness() {
            state.firebaseReadyGeneration += 1;
            state.firebaseReadyPromise = null;
        }

        async function initializeFirebase(generation) {
            const firebase = window.firebase;
            if (!firebase || !firebase.auth) {
                throw codedError(BLOCKED);
            }

            await loadRecaptchaLibrary();

            if (!firebase.apps || firebase.apps.length === 0) {
                firebase.initializeApp(config);
            }

            if (!firebaseAppCheckSiteKey) {
                throw codedError(UNCONFIGURED);
            }
            if (!firebase.appCheck) {
                throw codedError(BLOCKED);
            }
            if (!state.appCheckInitialized) {
                firebase.appCheck().activate(
                    new firebase.appCheck.ReCaptchaEnterpriseProvider(firebaseAppCheckSiteKey),
                    true,
                );
                state.appCheckInitialized = true;
            }

            const auth = firebase.auth();
            await auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);

            if (!state.recaptchaVerifier) {
                let verifier;
                try {
                    verifier = new firebase.auth.RecaptchaVerifier(
                        'recaptcha-container',
                        { size: 'invisible' }
                    );
                    const widgetId = await verifier.render();
                    if (generation !== state.firebaseReadyGeneration) {
                        verifier.clear();
                        throw codedError(BLOCKED);
                    }
                    state.recaptchaVerifier = verifier;
                    state.recaptchaWidgetId = widgetId;
                } catch (e) {
                    if (verifier && typeof verifier.clear === 'function') verifier.clear();
                    throw codedError(BLOCKED);
                }
            }

            if (generation !== state.firebaseReadyGeneration) throw codedError(BLOCKED);
            state.auth = auth;
            return auth;
        }

        // Drops the cached verifier so the next initializeFirebase() builds a
        // fresh widget. resetRecaptcha() only spends the current token.
        function clearRecaptchaVerifier() {
            if (state.recaptchaVerifier && typeof state.recaptchaVerifier.clear === 'function') {
                state.recaptchaVerifier.clear();
            }
            state.recaptchaVerifier = null;
            state.recaptchaWidgetId = null;
        }

        // A hidden challenge keeps its frame in the page, offscreen or at zero size.
        function recaptchaChallengeOpen() {
            return Array.prototype.some.call(
                document.querySelectorAll(RECAPTCHA_CHALLENGE_SELECTOR),
                (frame) => {
                    if (window.getComputedStyle(frame).visibility !== 'visible') return false;
                    const box = frame.getBoundingClientRect();
                    return box.width > 0 && box.height > 0 && box.bottom > 0;
                },
            );
        }

        function resetRecaptcha() {
            if (
                state.recaptchaWidgetId !== null
                && window.grecaptcha
                && typeof window.grecaptcha.reset === 'function'
            ) {
                window.grecaptcha.reset(state.recaptchaWidgetId);
            }
        }

        async function requestSession(phone) {
            const response = await post('/phone-verification/session', {
                phone_e164: phone,
            });
            const data = await responseJson(response);

            if (!response.ok) {
                if (response.status === HTTP_CONFLICT && data && data.error && data.error.code === INVALID_STATE) {
                    recovery.start();
                    return new Promise(function () {});
                }

                const failure = apiFailure(response, data, 'session_failed', RESEND_COOLDOWN_SECONDS);
                throw codedError(failure.code, failure.retryAfter);
            }

            if (data.status === 'waived') {
                completeVerification();
                return null;
            }

            if (data.sendable !== true) {
                showUnavailable();
                return null;
            }

            if (!data.nonce) {
                throw codedError('nonce_missing');
            }

            return data;
        }

        async function boundedPhase(promise, phase) {
            const result = await withDeadline(promise, PHASE_TIMEOUT_MS, window);
            if (result.timedOut) {
                const error = codedError('client_timeout');
                error.message = phase;
                reportVerifierFailure(error, true);
                error.reported = true;
                throw error;
            }
            if (result.error) throw result.error;

            return result.value;
        }

        function monotonicNow() {
            return window.performance && typeof window.performance.now === 'function'
                ? window.performance.now()
                : Date.now();
        }

        function clearPendingLock() {
            if (pendingStorageKey) safeStorageRemove(pendingStorageKey, SESSION_STORAGE);
        }

        function persistPendingLock(durationMs) {
            if (pendingStorageKey && durationMs > 0) {
                safeStorageSet(
                    pendingStorageKey,
                    String(Date.now() + durationMs),
                    SESSION_STORAGE,
                );
            }
        }

        function clearFallbackLock() {
            if (fallbackStorageKey) safeStorageRemove(fallbackStorageKey, SESSION_STORAGE);
        }

        const sendRecovery = createSendRecoveryCoordinator({
            clearDeadline: clearPendingLock,
            now: monotonicNow,
            timers: window,
            wallNow: Date.now,
            writeDeadline: (expiresAt) => {
                if (pendingStorageKey) {
                    safeStorageSet(pendingStorageKey, String(expiresAt), SESSION_STORAGE);
                }
            },
        });

        const confirmationRecovery = createSendRecoveryCoordinator({
            clearDeadline() {},
            now: monotonicNow,
            timers: window,
            wallNow: Date.now,
            writeDeadline() {},
        });

        function invalidatePendingWork(retainDeadline) {
            state.lifecycleGeneration += 1;
            state.sessionGeneration += 1;
            invalidateFirebaseReadiness();
            clearRecaptchaVerifier();
            recovery.clear();
            confirmationRecovery.clear();
            finishConfirmation();
            if (retainDeadline) sendRecovery.suspend();
            else sendRecovery.clear();
        }

        function unlockSend() {
            state.sending = false;
            setSendStalled(false);
            setBusy(sendButton, sendSpinnerId, false);
            setBusy(smsAlternativeButton, smsAlternativeSpinnerId, false);
            setPhoneEditBusy(false);
            cooldown.setSendBusy(false);
            setResendBusy(false);
            if (confirmButton) confirmButton.disabled = false;
            if (changeNumberButton) changeNumberButton.disabled = false;
        }

        // What is left of the held session's restore window, which stays under
        // the server's own session TTL.
        function remainingRetryableMs() {
            const age = Date.now() - state.sessionStoredAt;
            return Math.max(0, RETRYABLE_SESSION_TTL_MS - age);
        }

        // `reuse` sends on the session already held instead of minting one, so
        // the SMS alternative reaches Firebase without spending a second daily
        // session. `preferSms` is the caller asking for the SMS alternative
        // rather than the page's own send.
        function ownSessionRequest(phone, preferSms, reuse) {
            startPhoneSendAttempt({
                coordinator: sendRecovery,
                onExpire: unlockSend,
                onFailure(error) {
                    resetRecaptcha();
                    showFailure(error, true);
                    unlockSend();
                    if (window.console) window.console.warn('verify-phone:', error);
                },
                onProviderAccepted(confirmationResult) {
                    recovery.clear();
                    resetRecaptcha();
                    state.confirmationResult = confirmationResult;
                    closeFallbackOffer();
                    state.firebaseRetryable = false;
                    // Firebase now owns the code on the handset, so the confirm
                    // goes back to the Firebase endpoint and the control that
                    // asked for another code resends rather than starting over.
                    state.fallbackChannel = '';
                    setSmsAlternativeHidden(true);
                    cooldown.setIdleLabel(resendDefaultLabel);
                    setHidden(changeNumberButton, false);
                    forgetRetryableSession();
                    setHidden(resendButton, false);
                    showCodeForm();
                    startResendCooldown();
                    unlockSend();
                },
                onSessionAccepted(session, durationMs) {
                    state.sessionGeneration += 1;
                    state.phone = phone;
                    state.fallbackChannel = '';
                    state.channels = offeredChannels(session.channels, FALLBACK_CHANNELS);
                    state.fallbackDeadlineAt = Date.now() + durationMs;
                    state.fallbackDeadlineMonotonic = monotonicNow() + durationMs;
                    setHidden(fallbackBox, true);
                    if (reuse) return;

                    state.sessionOffer = Array.isArray(session.channels)
                        ? session.channels.slice()
                        : null;
                    state.nonce = String(session.nonce);
                    // A paid session clears `reverse_token_hash`, so a token the
                    // panel still holds may no longer verify. A tap rejoins the
                    // live session at no cost and returns its token.
                    reverse.dropChallenge();
                    state.sessionChannels = state.channels.slice();
                    state.route = typeof session.route === 'string' ? session.route : '';
                    state.routeOffered = false;
                    state.sessionStoredAt = Date.now();
                    // Keep this charged session until Firebase accepts its send,
                    // across a reload as well, so a client that cannot reach
                    // Firebase does not buy a new session on every page load.
                    state.firebaseRetryable = true;
                    rememberRetryableSession();
                },
                onTimeout(phase) {
                    const timeout = codedError('client_timeout');
                    timeout.message = phase;
                    reportVerifierFailure(timeout, true);
                    hideMessages();
                    setHidden(sendUncertainBox, false);
                    setHidden(document.getElementById(sendSpinnerId), true);
                    const sendPhase = phase !== 'session';
                    if (sendPhase) setResendBusy(false);
                    // Only a send phase holds a live session for the card to
                    // join. The code step keeps the card as its alternate.
                    if (sendPhase && !phoneForm.classList.contains('hidden')) setSendStalled(true);
                },
                providerReady: () => ensureFirebase(),
                readyTimeoutMs: VERIFIER_READY_TIMEOUT_MS,
                holdDeadline: recaptchaChallengeOpen,
                // Firebase is initialised here rather than before the session,
                // because only the server knows the route and a WhatsApp-route
                // number never initialises Firebase at all.
                providerSend: () => ensureFirebase().then((auth) => {
                    // Cleared only once Firebase answers. A session failure or
                    // an unreachable Firebase must leave a token from an
                    // earlier confirm usable, so a caller who already proved
                    // the handset can finish without a new code.
                    state.firebaseIdToken = '';
                    state.firebaseUser = null;

                    return auth.signInWithPhoneNumber(phone, state.recaptchaVerifier);
                }),
                requestSession: () => (reuse
                    ? Promise.resolve({
                        nonce: state.nonce,
                        expires_in: Math.ceil(remainingRetryableMs() / 1000),
                    })
                    : requestSession(phone)),
                // The lead sender is read off the session. A session offers its
                // lead once, so the second send on it is the SMS alternative.
                shouldSend(session) {
                    // The gate, not a hint. The send button keys on the picked
                    // country, and a partial calling code in the dial field
                    // leaves that country behind while the composed number goes
                    // to the server. Only the server's answer for the posted
                    // number can refuse the send that number would bill.
                    const offer = Array.isArray(session.channels)
                        ? session.channels
                        : state.sessionOffer;
                    if (!offerCarriesPaidSend(offer)) {
                        refusePaidSendForCorridor(phone);
                        return false;
                    }

                    if (state.route !== 'whatsapp' || state.routeOffered || preferSms) return true;

                    state.routeOffered = true;
                    offerFallback(['whatsapp'], true);
                    return false;
                },
                sessionLifetimeMs: SESSION_LIFETIME_MS,
                timeoutMs: PHASE_TIMEOUT_MS,
            });
        }

        // A corridor the server serves with no paid channel. The held session
        // must not stay reusable: `reuseSession` tests `firebaseRetryable`, and a
        // reuse brings no offer of its own.
        function refusePaidSendForCorridor(phone) {
            // The hint keyed on the picked country invited this press. The
            // answer for the number is now in hand, so the button follows it and
            // stops inviting a second one.
            state.refusedPhone = phone;
            applyPaidSendWithdrawal();
            state.firebaseRetryable = false;
            forgetRetryableSession();
            hideMessages();
            // The card is the route in this corridor, so it takes the screen the
            // refused send would have held.
            if (reverseConfigured) reverse.start();
        }

        // `preferSms` is the caller saying the user picked the SMS
        // alternative rather than the page's own send.
        async function sendCode(preferSms) {
            if (state.sending || state.confirming || reverse.busy() || paidSendWithdrawn()) return;
            // Implicit submission reaches here even where the disabled button
            // does not, so the guard sits on the action rather than the button.
            if (
                (holdsTheField(fallbackBox) || holdsTheField(verifierRetryBox))
                && !state.firebaseRetryable
            ) return;
            const lifecycleGeneration = state.lifecycleGeneration;

            const phone = currentPhone();
            hideMessages();

            if (!PHONE_PATTERN.test(phone)) {
                showError('number');
                return;
            }

            state.sending = true;
            let generation = state.sessionGeneration;
            setBusy(sendButton, sendSpinnerId, true);
            setBusy(smsAlternativeButton, smsAlternativeSpinnerId, true);
            setPhoneEditBusy(true);
            cooldown.setSendBusy(true);
            setResendBusy(true);
            if (confirmButton) confirmButton.disabled = true;
            if (changeNumberButton) changeNumberButton.disabled = true;

            try {
                // The session call leads, because only the server knows which
                // sender this number routes to, and a WhatsApp-route number
                // never initialises Firebase at all. That order makes the
                // session the first thing spent, so a session Firebase never
                // sent on is held and reused rather than bought again.
                const reuseSession = state.firebaseRetryable
                    && state.nonce
                    && retryableSessionIsLive()
                    && matchesSessionPhone(state.phone, phone);
                // A caller who asked for SMS is never answered with the WhatsApp
                // lead. The held session may have expired, and the session
                // minted to replace it arrives with its lead unoffered again.
                if (!state.pageActive || lifecycleGeneration !== state.lifecycleGeneration) return;
                ownSessionRequest(phone, preferSms, reuseSession);
                return;
            } catch (e) {
                if (!state.pageActive || lifecycleGeneration !== state.lifecycleGeneration) return;
                if (e && e.code === 'client_timeout' && e.message === 'readiness') {
                    invalidateFirebaseReadiness();
                }
                showFailure(e, true);
                if (window.console) {
                    window.console.warn('verify-phone:', e);
                }
            } finally {
                if (!state.pageActive || lifecycleGeneration !== state.lifecycleGeneration) return;
                // A reCAPTCHA token is single-use, and the verifier is built once
                // and reused, so the widget is reset after every attempt. Resetting
                // only on failure leaves a resend replaying a spent token, which
                // Firebase rejects as an invalid app credential.
                if (!sendRecovery.active) {
                    resetRecaptcha();
                    state.sending = false;
                    setBusy(sendButton, sendSpinnerId, false);
                    setBusy(smsAlternativeButton, smsAlternativeSpinnerId, false);
                    setPhoneEditBusy(false);
                    cooldown.setSendBusy(false);
                    setResendBusy(false);
                    if (confirmButton) confirmButton.disabled = false;
                    if (changeNumberButton) changeNumberButton.disabled = false;
                }
            }
        }

        async function confirmCode() {
            if (state.confirming || state.sending || reverse.busy()) return;

            const code = codeInput.value.replace(/\s+/g, '');
            hideMessages();

            const viaFallback = Boolean(state.fallbackChannel);
            const firebaseReady = state.firebaseIdToken
                || (CODE_PATTERN.test(code) && state.confirmationResult);

            if (!state.nonce || (viaFallback ? !CODE_PATTERN.test(code) : !firebaseReady)) {
                showError('code');
                return;
            }

            state.confirming = true;
            setBusy(confirmButton, confirmSpinnerId, true);
            cooldown.setSendBusy(true);
            // Disable without the spinner. Only a send animates the resend icon.
            cooldown.setResendBusy(true);
            if (changeNumberButton) changeNumberButton.disabled = true;

            const remainingMs = state.fallbackDeadlineMonotonic - monotonicNow();
            if (remainingMs <= 0) {
                finishConfirmation();
                expireFallbackSession();
                return;
            }

            const request = viaFallback
                ? requestFallbackConfirmation(code)
                : confirmWithCachedToken({
                    code,
                    postConfirmation: (firebaseIdToken, nonce) => post('/phone-verification/confirm', {
                        firebase_id_token: firebaseIdToken,
                        nonce,
                    }),
                    responseJson,
                    state,
                    warn: (error) => {
                        if (window.console) window.console.warn('verify-phone signout:', error);
                    },
                });

            confirmationRecovery.own({
                durationMs: remainingMs,
                onExpire() {
                    state.confirmationUncertain = false;
                    expireFallbackSession();
                },
                onReject(error) {
                    state.confirmationUncertain = false;
                    showFailure(error, false);
                    if (!state.firebaseIdToken) resetCodeInput();
                    finishConfirmation();
                },
                onResolve(result) {
                    if (result.stale) return;
                    if (result.verified) {
                        state.confirmationUncertain = false;
                        completeVerification();
                        return;
                    }
                    if (viaFallback && !answersTheSubmittedCode(result)) {
                        beginFallbackConfirmationRecovery();
                        return;
                    }
                    state.confirmationUncertain = false;
                    // Only a refusal that answered the code retires it. A rate
                    // limit stopped the check from running, so the typed code
                    // is still the caller's best guess and survives the wait.
                    if (result.terminal) resetCodeInput();
                    const failure = apiFailure(
                        result.response,
                        result.data,
                        'confirm_failed',
                        RESEND_COOLDOWN_SECONDS,
                    );
                    showFailure(codedError(failure.code, failure.retryAfter), false);
                    finishConfirmation();
                },
                onTimeout() {
                    state.confirmationUncertain = true;
                    finishConfirmation();
                    hideMessages();
                    setHidden(sendUncertainBox, false);
                    recovery.start();
                },
                promise: request,
                retainTimedOutRejection: true,
                timeoutMs: Math.min(PHASE_TIMEOUT_MS, remainingMs),
            });
        }

        // Whether a refused fallback confirm answers the code that was typed.
        // A refusal about the code, and a rate limit that stopped the check
        // from happening at all, both belong on screen. `invalid_state` says
        // the session moved instead, and only the status poll knows what it
        // moved to. An uncertain outcome, a 5xx or a timeout, recovers as well.
        function answersTheSubmittedCode(result) {
            if (result.response.status === HTTP_TOO_MANY_REQUESTS) return true;
            if (!result.terminal) return false;

            const code = result.data && result.data.error && result.data.error.code;

            return code !== INVALID_STATE;
        }

        function beginFallbackConfirmationRecovery() {
            state.confirmationUncertain = true;
            finishConfirmation();
            setHidden(codeForm, true);
            codeInput.value = '';
            renderCodeSlots();
            hideMessages();
            setHidden(sendUncertainBox, false);
            recovery.start();
        }

        function finishConfirmation() {
                state.confirming = false;
                setBusy(confirmButton, confirmSpinnerId, false);
                cooldown.setSendBusy(false);
                cooldown.setResendBusy(false);
                if (changeNumberButton) changeNumberButton.disabled = false;
        }

        function resetFreshPhoneSession() {
            invalidatePendingWork(false);
            clearFallbackExpiry();
            clearPendingLock();
            clearFallbackLock();
            state.confirmationResult = null;
            state.confirmationUncertain = false;
            state.firebaseIdToken = '';
            state.firebaseUser = null;
            state.nonce = '';
            state.phone = '';
            state.fallbackChannel = '';
            state.fallbackRequestUncertain = false;
            state.fallbackChannels = [];
            state.fallbackChannelsResolved = false;
            setHidden(codeForm, true);
            setHidden(fallbackBox, true);
            fallbackButtons.forEach(function (button) {
                button.disabled = true;
                setHidden(button, true);
            });
            setFallbackBusy(false);
            unlockSend();
            restorePhoneForm();
            hideMessages();
            if (reverseConfigured) reverse.start();
        }

        // The provider that delivered the code is the one that checks it, so a
        // code from the fallback route never reaches the Firebase confirm.
        async function requestFallbackConfirmation(code) {
            if (state.fallbackDeadlineMonotonic <= monotonicNow()) {
                return { stale: true };
            }
            const sessionGeneration = state.sessionGeneration;
            const response = await post('/phone-verification/fallback/confirm', {
                nonce: state.nonce,
                code: code,
            });
            const data = await responseJson(response);
            if (sessionGeneration !== state.sessionGeneration) return { stale: true };
            return {
                data,
                response,
                stale: false,
                terminal: !response.ok && response.status !== HTTP_TOO_MANY_REQUESTS
                    && response.status < 500,
                verified: response.ok,
            };
        }

        fallbackButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                sendFallback(button.getAttribute('data-channel'));
            });
        });

        phoneForm.addEventListener('submit', function (event) {
            event.preventDefault();
            sendCode();
        });

        codeForm.addEventListener('submit', function (event) {
            event.preventDefault();
            confirmCode();
        });

        if (codeSlots.length) {
            // Paste lands here too, since a pasted code fires `input` on the
            // single underlying field. Non-digits are dropped, so a code copied
            // as "123 456" or "code: 123456" still resolves.
            codeInput.addEventListener('input', function () {
                const digits = digitsOnly(codeInput.value).slice(0, CODE_LENGTH);
                if (codeInput.value !== digits) {
                    codeInput.value = digits;
                }

                renderCodeSlots();

                // A complete code submits itself. Retyping the last digit is the
                // whole remaining step, so making the user reach for the button
                // is friction with no purpose.
                if (digits.length === CODE_LENGTH && !state.confirming) {
                    confirmCode();
                }
            });

            codeInput.addEventListener('focus', function () {
                pinCodeCaret();
                renderCodeSlots();
            });

            codeInput.addEventListener('blur', renderCodeSlots);

            // Clicking a box focuses the field rather than moving the caret into
            // the middle of the value.
            codeInput.addEventListener('click', pinCodeCaret);

            codeInput.addEventListener('keyup', renderCodeSlots);
        }

        if (fallbackChangeNumberButton) {
            fallbackChangeNumberButton.addEventListener('click', changeNumber);
        }

        if (verifierRetryButton) {
            verifierRetryButton.addEventListener('click', retryVerifier);
        }

        if (smsAlternativeButton) {
            // The page's own send is the Firebase SMS route. It reuses the open
            // session, and mints one only once that session has expired.
            smsAlternativeButton.addEventListener('click', function () {
                sendCode(true);
            });
        }

        if (resendButton) {
            resendButton.addEventListener('click', function () {
                // The fallback code came from a provider this session already
                // spent. Another one needs a new session.
                if (state.fallbackChannel) {
                    changeNumber();
                    return;
                }
                if (!state.phone) {
                    changeNumber();
                    return;
                }
                codeInput.value = '';
                renderCodeSlots();
                sendCode();
            });
        }

        if (changeNumberButton) {
            changeNumberButton.addEventListener('click', function () {
                changeNumber();
            });
        }

        // A session created with no number. The corridor comes from the country
        // the picker holds, because no number is there to derive one from.
        async function requestReverseSession(country) {
            const body = country ? { phone_country: country } : {};
            let response = await post('/phone-verification/session', body);
            // Recovery's status read holds the same per-user lock on a restored
            // stall, so one collision on the card's join is retried.
            if (response.status === HTTP_SERVICE_UNAVAILABLE && state.sendStalled) {
                const lifecycleGeneration = state.lifecycleGeneration;
                await new Promise((resolve) => window.setTimeout(resolve, STALLED_JOIN_RETRY_MS));
                const current = state.pageActive && lifecycleGeneration === state.lifecycleGeneration;
                if (state.sendStalled && current) response = await post('/phone-verification/session', body);
            }
            const data = await responseJson(response);

            if (!response.ok) {
                // The gate can clear in another tab while the card is the offer
                // on screen. Its sibling recovers from that, so this does too.
                // The code is thrown rather than left pending, because the
                // caller owns a busy flag only its own finally releases.
                if (response.status === HTTP_CONFLICT && data && data.error && data.error.code === INVALID_STATE) {
                    recovery.start();
                    throw codedError(INVALID_STATE);
                }

                // A rate-limit refusal gets the same named copy the send
                // button's does.
                const failure = apiFailure(response, data, 'session_failed');
                throw codedError(failure.code, failure.retryAfter);
            }
            if (data.status === 'waived') {
                completeVerification();
                return new Promise(function () {});
            }

            return data;
        }

        // Emphasis follows the lead, and the two offers share one pair of
        // treatments with the paid fallback buttons so they cannot drift.
        function setEmphasis(element, filled) {
            if (!element) return;

            const drop = filled ? FALLBACK_SECONDARY_CLASSES : FALLBACK_PRIMARY_CLASSES;
            const take = filled ? FALLBACK_PRIMARY_CLASSES : FALLBACK_SECONDARY_CLASSES;
            drop.forEach((name) => element.classList.remove(name));
            take.forEach((name) => element.classList.add(name));
        }

        /**
         * The card's three resting places, each named by a neighbour rather
         * than by "the end", so the served order stays reachable and the tab
         * order follows what the user sees.
         *
         * The parent is the wrapper, never the card's current parent: the code
         * step moves the card into the code form, and reading the parent back
         * off the node would then insert it before a node that is not its
         * sibling.
         */
        function reverseCardAnchor(step, leads) {
            if (step === 'code') return { anchor: changeNumberButton, parent: codeForm };
            return { anchor: leads ? phoneForm : codeForm, parent: stepWrapper };
        }

        function setReverseLeadLayout(leads, paidOffered, step) {
            if (!reverseBox || !stepWrapper) return false;

            // Re-inserting the node blurs whatever is focused inside it and
            // restarts its spinner, so a live challenge holds its order inside
            // the parent it already sits in. It never holds the parent: the
            // card would then stay in the form the step just hid.
            // A stalled send leaves the card as the only route, so it moves even then.
            const live = reverse.state.phase === 'challenge' && !state.sendStalled;
            const placement = reverseCardAnchor(step, leads);
            if (placement.parent && placement.anchor) {
                const misparented = reverseBox.parentNode !== placement.parent;
                const misordered = reverseBox.nextElementSibling !== placement.anchor;
                if (misparented || (!live && misordered)) {
                    const focused = reverseBox.contains(document.activeElement) ? document.activeElement : null;
                    placement.parent.insertBefore(reverseBox, placement.anchor);
                    if (focused) focused.focus({ preventScroll: true });
                }
            }
            // A live challenge can hold the move back, so these read the position
            // the card actually holds rather than the one the corridor asked for.
            // Otherwise the heading names a lead the card does not have.
            const placed = step === 'phone' && reverseBox.nextElementSibling === phoneForm;
            setEmphasis(reverseShowButton, placed);
            setEmphasis(sendButton, !placed);
            // The heading names the paid send under the card, so it is true only
            // where the card leads AND that send exists. It hides by attribute,
            // because the form's `space-y-5` skips `[hidden]` children.
            if (paidAlternativeHeading) paidAlternativeHeading.hidden = !(placed && paidOffered);

            return placed;
        }

        const reverse = createReverseVerification({
            createQrSvg,
            createSession: requestReverseSession,
            elements: {
                challenge: document.getElementById('verify-phone-reverse-challenge'),
                copyButton: document.getElementById('verify-phone-reverse-copy'),
                copyLabel: document.getElementById('verify-phone-reverse-copy-label'),
                countdown: document.getElementById('verify-phone-reverse-countdown'),
                heading: document.getElementById('verify-phone-reverse-heading'),
                linkList: document.getElementById('verify-phone-reverse-links'),
                links: {
                    telegram: document.getElementById('verify-phone-reverse-telegram'),
                    whatsapp: document.getElementById('verify-phone-reverse-whatsapp'),
                },
                noSms: document.getElementById('verify-phone-reverse-no-sms'),
                offer: document.getElementById('verify-phone-reverse-offer'),
                panel: reverseBox,
                qrFigures: {
                    telegram: document.getElementById('verify-phone-reverse-qr-telegram'),
                    whatsapp: document.getElementById('verify-phone-reverse-qr-whatsapp'),
                },
                qrHosts: {
                    telegram: document.getElementById('verify-phone-reverse-qr-telegram-image'),
                    whatsapp: document.getElementById('verify-phone-reverse-qr-whatsapp-image'),
                },
                qrList: document.getElementById('verify-phone-reverse-qr'),
                showButton: reverseShowButton,
                spinner: document.getElementById('verify-phone-reverse-spinner'),
                startAgainButton: document.getElementById('verify-phone-reverse-start-again'),
                timeout: document.getElementById('verify-phone-reverse-timeout'),
                timeoutText: document.getElementById('verify-phone-reverse-timeout-text'),
                token: document.getElementById('verify-phone-reverse-token'),
                waiting: document.getElementById('verify-phone-reverse-waiting'),
            },
            initIcons: (element) => window.LucideIcons?.initialize(element),
            isReverseLead: (iso) => isReverseLeadCountry(iso, paidLeadCountries),
            isReverseOnly: (iso) => isReverseOnlyCountry(iso, reverseOnlyCountries),
            now: Date.now,
            onCreateRefused(error) {
                const code = error && error.code;
                // Recovery owns the page from here, so the card keeps its offer
                // rather than reporting a failure over the top of it.
                if (code === INVALID_STATE) return true;
                // A reverse create reserves no budget and sends nothing, so it
                // can exhaust neither a budget nor a cooldown.
                if (code !== RATE_LIMITED) return false;

                showFailure(Object.assign(error, { reported: true }), false);
                return true;
            },
            onPaidSendOffered: applyPaidSendWithdrawal,
            // A stalled send holds no request the card's lock would race.
            paidSendBusy: () => (state.sending && !state.sendStalled) || state.confirming,
            sendStalled: () => state.sendStalled,
            onReverseLeads: setReverseLeadLayout,
            onVerified: completeVerification,
            onWaived: completeWithProviderWaiver,
            requestStatus: async () => {
                const response = await get('/phone-verification');

                return { data: await responseJson(response), response: response };
            },
            selectedCountry: selectedCountryIso,
            storage: {
                read: () => (reverseStorageKey
                    ? safeStorageGet(reverseStorageKey, SESSION_STORAGE)
                    : null),
                remove: () => {
                    if (reverseStorageKey) safeStorageRemove(reverseStorageKey, SESSION_STORAGE);
                },
                write: (value) => {
                    if (reverseStorageKey) safeStorageSet(reverseStorageKey, value, SESSION_STORAGE);
                },
            },
            timers: window,
            trans: (key, params) => {
                const text = String(messages[key] || '');

                return params && params.time !== undefined
                    ? text.replace('{time}', params.time)
                    : text;
            },
            visibility: {
                isHidden: () => document.hidden === true,
                subscribe: (listener) => document.addEventListener('visibilitychange', listener),
            },
        });

        if (hasCountryPicker) {
            // The server resolves the country from the request IP. Fall back to the
            // built-in default when it has none, or names one with no dial code.
            const geoIso = (root.getAttribute('data-default-country') || '').toLowerCase();
            applyCountry(countryByIso(geoIso) || countryByIso(DEFAULT_COUNTRY_ISO));

            createCountryListbox({
                button: countryButton,
                countries,
                createOption: countryOption,
                documentTarget: document,
                empty: countryEmpty,
                getSelectedIso: function () {
                    return state.country ? state.country.iso : '';
                },
                list: countryList,
                onSelect: applyCountry,
                panel: countryPanel,
                phoneInput,
                search: countrySearch,
                setHidden,
                wrapper: countryWrapper,
            });

            phoneInput.addEventListener('input', function (event) {
                absorbInternationalNumber(event.inputType === 'insertFromPaste');
                onNumberRewritten();
            });

            countryDial.addEventListener('input', function () {
                const digits = digitsOnly(countryDial.value);
                // Keep the field showing a '+' prefix without fighting the caret on
                // every keystroke: only rewrite when the user dropped it.
                if (countryDial.value !== '+' + digits) {
                    countryDial.value = '+' + digits;
                }

                resizeDialInput();
                syncCountryFromDialInput();
                onNumberRewritten();
            });

            // A dial code typed to completion moves on to the number, the same way
            // picking one from the list does.
            countryDial.addEventListener('keydown', function (event) {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    phoneInput.focus();
                }
            });

        }

        function showRecoveryLock(hasDeadline) {
            state.sending = true;
            restorePhoneForm();
            hideMessages();
            setHidden(hasDeadline ? sendUncertainBox : unavailableBox, false);
            // The box comes back with the line and the description the stall set.
            setSendStalled(hasDeadline && state.sendStalled);
            setBusy(sendButton, sendSpinnerId, false);
            cooldown.setSendBusy(true);
            setResendBusy(true);
            if (changeNumberButton) changeNumberButton.disabled = true;
        }

        function handleBootstrapResult(result) {
            const response = result.response;
            const data = result.data;
            const fallbackSession = response.ok && data && data.fallback_session;
            const activeSession = response.ok && data && data.active_session;

            finishInitialStatusRecovery();
            state.statusResolved = true;

            if (response.ok && data && data.required === false) {
                recovery.clear();
                clearPendingLock();
                clearFallbackLock();
                if (data.status === 'waived') {
                    completeWithProviderWaiver();
                } else {
                    completeVerification();
                }
                return;
            }

            if (
                state.confirmationUncertain
                && activeSession
                && activeSession.fallback_send_state === 'available'
                && Number(activeSession.expires_in) > 0
            ) {
                state.confirmationUncertain = false;
                state.fallbackChannel = '';
                state.confirmationResult = null;
                clearPendingLock();
                clearFallbackLock();
                unlockSend();
                setFallbackBusy(false);
                hideMessages();
                if (!state.phone || !state.fallbackChannelsResolved) {
                    resetFreshPhoneSession();
                    return true;
                }
                offerFallback(state.channels, state.firebaseRetryable);
                return true;
            }

            // A retryable fallback send can finish without a durable claim. The
            // authoritative status is the only safe signal that choices are open.
            if (
                state.fallbackRequestUncertain
                && activeSession
                && activeSession.fallback_send_state === 'available'
                && Number(activeSession.expires_in) > 0
            ) {
                state.fallbackRequestUncertain = false;
                sendRecovery.clear();
                recovery.clear();
                clearPendingLock();
                clearFallbackLock();
                unlockSend();
                setFallbackBusy(false);
                hideMessages();
                offerFallback(state.channels, state.firebaseRetryable);
                return true;
            }

            if (
                activeSession
                && activeSession.fallback_send_state === 'pending'
                && Number(activeSession.expires_in) > 0
            ) {
                const durationMs = Number(activeSession.expires_in) * 1000;
                if (pendingStorageKey) {
                    safeStorageSet(
                        pendingStorageKey,
                        String(Date.now() + durationMs),
                        SESSION_STORAGE,
                    );
                }
                setHidden(codeForm, true);
                showRecoveryLock(true);
                return false;
            }

            if (
                fallbackSession
                && fallbackSession.nonce
                && (fallbackSession.channel === 'sms' || fallbackSession.channel === 'whatsapp')
                && Number(fallbackSession.expires_in) > 0
            ) {
                if (state.confirmationUncertain) {
                    // The status endpoint holds the challenge lock. A confirmable
                    // fallback therefore means the prior server claim ended.
                    if (activeSession && activeSession.fallback_send_state === 'confirmable') {
                        state.confirmationUncertain = false;
                    } else {
                        const durationMs = Number(fallbackSession.expires_in) * 1000;
                        state.fallbackDeadlineAt = Date.now() + durationMs;
                        state.fallbackDeadlineMonotonic = monotonicNow() + durationMs;
                        if (fallbackStorageKey) {
                            safeStorageSet(
                                fallbackStorageKey,
                                String(state.fallbackDeadlineAt),
                                SESSION_STORAGE,
                            );
                        }
                        armFallbackExpiry(durationMs);
                        showRecoveryLock(true);
                        return false;
                    }
                }
                sendRecovery.clear();
                recovery.clear();
                clearPendingLock();
                unlockSend();
                state.fallbackDeadlineAt = Date.now() + (Number(fallbackSession.expires_in) * 1000);
                state.fallbackDeadlineMonotonic = monotonicNow()
                    + (Number(fallbackSession.expires_in) * 1000);
                armFallbackExpiry(Number(fallbackSession.expires_in) * 1000);
                if (fallbackStorageKey) {
                    safeStorageSet(
                        fallbackStorageKey,
                        String(state.fallbackDeadlineAt),
                        SESSION_STORAGE,
                    );
                }
                state.sessionGeneration += 1;
                state.nonce = String(fallbackSession.nonce);
                state.fallbackChannel = fallbackSession.channel;
                state.phone = '';
                setHidden(fallbackBox, true);
                fallbackButtons.forEach(function (button) {
                    button.disabled = true;
                });
                if (resendButton && resendButton.getAttribute('data-hide-on-resumed-fallback') === 'true') {
                    setHidden(resendButton, true);
                }
                showCodeForm();
                return true;
            }

            // A session opened by the card alone has no paid send to recover, so
            // the lock would strand a user whose token is still good.
            //
            // `has_reverse` cannot tell the two apart: the paid create mints a
            // reverse token too, and a Firebase send is client-side, so the
            // server still reports the send as available. This browser's own
            // deadline keys are the record of a send it started.
            const reverseOutstanding = response.ok && data && data.reverse && data.reverse.has_reverse === true;
            const sendStarted = (pendingStorageKey && safeStorageGet(pendingStorageKey, SESSION_STORAGE))
                || (fallbackStorageKey && safeStorageGet(fallbackStorageKey, SESSION_STORAGE));
            if (reverseConfigured && reverseOutstanding && activeSession && !sendStarted
                && !state.confirmationUncertain && !state.fallbackRequestUncertain
                && activeSession.fallback_send_state === 'available') {
                recovery.clear();
                clearPendingLock();
                clearFallbackLock();
                unlockSend();
                hideMessages();
                restorePhoneForm();
                reverse.start();
                return true;
            }

            if (activeSession && Number(activeSession.expires_in) > 0) {
                const durationMs = Number(activeSession.expires_in) * 1000;
                if (pendingStorageKey) {
                    safeStorageSet(
                        pendingStorageKey,
                        String(Date.now() + durationMs),
                        SESSION_STORAGE,
                    );
                }
                showRecoveryLock(true);
                // A reload or a back-forward cache return dropped the send's
                // promise, so its outcome can never reach this page. A held
                // confirm or a send still owned here is not that case.
                if (!state.confirmationUncertain && !state.fallbackRequestUncertain && !sendRecovery.active) {
                    setSendStalled(true);
                }
                return false;
            }

            resetFreshPhoneSession();
            return true;
        }

        const recovery = createPhoneVerificationRecovery({
            clearFallbackDeadline: clearFallbackLock,
            clearPendingDeadline: clearPendingLock,
            now: Date.now,
            onBlocked: showRecoveryLock,
            onAttemptsExhausted() {
                finishInitialStatusRecovery();
                state.statusResolved = true;
                unlockSend();
                restorePhoneForm();
                showUnavailable();
            },
            onDeadlineExpired() {
                finishInitialStatusRecovery();
                if (state.statusResolved) {
                    unlockSend();
                    restorePhoneForm();
                } else {
                    showRecoveryLock(false);
                }
            },
            onResult: handleBootstrapResult,
            onTimeout() {
                const error = codedError('client_timeout');
                error.message = 'resume';
                reportVerifierFailure(error, true);
            },
            readFallbackDeadline: () => fallbackStorageKey
                ? safeStorageGet(fallbackStorageKey, SESSION_STORAGE)
                : null,
            readPendingDeadline: () => pendingStorageKey
                ? safeStorageGet(pendingStorageKey, SESSION_STORAGE)
                : null,
            requestStatus: async (signal) => {
                const token = await getApiToken();
                if (signal.aborted) throw new Error('status_request_cancelled');
                if (!token) throw new Error('api_token_unavailable');
                const response = await get('/phone-verification', { signal });
                return { response: response, data: await responseJson(response) };
            },
            retryMs: PHASE_TIMEOUT_MS,
            timers: window,
        });

        function startStatusRecovery() {
            if (initialStatusRecoveryPending) setStatusLoading(true);
            recovery.start();
        }

        startStatusRecovery();
        if (typeof window.addEventListener === 'function') {
            window.addEventListener('pagehide', function () {
                state.pageActive = false;
                reverse.hide();
                if (confirmationRecovery.active) state.confirmationUncertain = true;
                finishInitialStatusRecovery();
                invalidatePendingWork(true);
            });
            window.addEventListener('pageshow', function (event) {
                if (!event.persisted) return;
                state.pageActive = true;
                if (state.confirmationUncertain) {
                    startStatusRecovery();
                    return;
                }
                const confirmationRemainingMs = state.fallbackDeadlineMonotonic - monotonicNow();
                if (state.confirmationResult && state.nonce && confirmationRemainingMs > 0) {
                    armFallbackExpiry(confirmationRemainingMs);
                    unlockSend();
                    showCodeForm();
                    return;
                }
                if (state.confirmationResult) {
                    state.confirmationResult = null;
                    state.nonce = '';
                }
                startStatusRecovery();
            });
        }
    });
})();
